"use client";

import React from "react";
import Link from "next/link";
import { getImageUrl } from "@/utils/image";
import { type LibraryArticle } from "@/types/libraryType";

interface LibraryCardProps {
  article: LibraryArticle;
  size?: "small" | "medium" | "large";
}

export function LibraryCard({ article, size = "medium" }: LibraryCardProps) {
  const { title, img, createdAt } = article;

  const sizeClasses = {
    small: {
      container: "max-w-sm",
      image: "h-32",
      title: "text-sm font-semibold",
      description: "text-xs",
    },
    medium: {
      container: "max-w-md",
      image: "h-48",
      title: "text-lg font-semibold",
      description: "text-sm",
    },
    large: {
      container: "max-w-lg",
      image: "h-100",
      title: "text-xl font-bold",
      description: "text-base",
    },
  };

  const classes = sizeClasses[size];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Category badge removed for this screen.

  return (
    <div
      className={`${classes.container} bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-gray-100 flex flex-col`}
    >
      {/* Image */}
      <div className={`relative ${classes.image} bg-gray-200 overflow-hidden`}>
        <img
          src={getImageUrl(img.url || "")}
          alt={title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col h-full">
        {/* Title */}
        <div
          className={`${classes.title} text-gray-900 leading-tight line-clamp-2 mb-4`}
        >
          {title}
        </div>

        <div className="mt-auto space-y-4">
          {/* Meta info */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <div className="flex items-center text-gray-500 text-sm">
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3a4 4 0 118 0v4M7 21h10a2 2 0 002-2V9a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              {formatDate(createdAt)}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Link
              href={`/library/${article.id}`}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              Xem chi tiết
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
